import { Order, TransactionStatus } from '@hero/schema';
import type {
  InvoiceUnifiedStatus,
  TransactionCustomerCategory,
  TransactionCarrierCategory,
} from '@hero/api';

// ─── Invoice Status Colors ──────────────────────────────────────────────────

/**
 * Get the appropriate CSS class for an invoice status badge.
 * Returns Tailwind CSS classes for background and text colors.
 *
 * Semantic color pattern:
 * - Amber/Warning: On-going (in progress)
 * - Green/Success: Paid (completed)
 * - Red/Error: Not paid / not_paid_or_partial (action required)
 * - Blue/Info: Partially paid (partial progress)
 * - Orange: Pending (awaiting action)
 * - Gray: Cancelled (inactive)
 * - Red (dark): Overdue (urgent)
 *
 * @param status - The invoice status string (supports InvoiceStatus and InvoiceUnifiedStatus)
 * @returns CSS class string for styling the status badge
 */
export const getInvoiceStatusBadgeClass = (status: InvoiceUnifiedStatus): string => {
  switch (status) {
    // Blue/Info: On-going (active/in progress)
    case 'on_going':
      return 'bg-blue-100 text-blue-700 hover:bg-blue-100 hover:text-blue-700';

    // Green/Success: Paid
    case 'paid':
      return 'bg-success-100 text-success hover:bg-success-100 hover:text-success';

    // Red (dark): Not paid / not_paid_or_partial (action required)
    case 'not_paid':
    case 'not_paid_or_partial':
      return 'bg-red-100 text-red-600 hover:bg-red-100 hover:text-red-600';

    // Purple: Partially paid
    case 'partially_paid':
      return 'bg-purple-100 text-purple-700 hover:bg-purple-100 hover:text-purple-700';

    // Orange: Pending
    case 'pending':
      return 'bg-orange-100 text-orange-600 hover:bg-orange-100 hover:text-orange-600';

    // Gray: Cancelled
    case 'cancelled':
      return 'bg-gray-100 text-gray-500 hover:bg-gray-100 hover:text-gray-500';

    // Red/Error: Overdue (urgent)
    case 'overdue':
      return 'bg-error-100 text-error hover:bg-error-100 hover:text-error';

    // Default fallback
    default:
      return 'bg-gray-100 text-gray-600 hover:bg-gray-100 hover:text-gray-600';
  }
};

// ─── Order Status Colors ────────────────────────────────────────────────────

/**
 * Get the appropriate CSS class for an order status badge
 * Returns Tailwind CSS classes for background and text colors
 *
 * Semantic color pattern based on status meaning:
 * - Gray/Neutral: New orders (pending action)
 * - Blue/Info: Accepted/Confirmed (information/acknowledged)
 * - Orange: Pickup states (in_transit_pickup, arrived_pickup)
 * - Purple: Dropoff states (in_transit_dropoff, arrived_dropoff)
 * - Green/Success: Delivered (successful completion)
 * - Red/Error: Canceled (failure/error)
 *
 * @param status - The order status from Order['status']['order']
 * @returns CSS class string for styling the status badge
 */
export const getOrderStatusBadgeClass = (status: Order['status']['order']): string => {
  switch (status) {
    // Gray/Neutral: New (pending)
    case 'new':
      return 'bg-gray-100 text-gray-700 hover:bg-gray-100 hover:text-gray-700';

    // Blue/Info: Accepted (acknowledged/information)
    case 'accepted':
      return 'bg-blue-100 text-blue-700 hover:bg-blue-100 hover:text-blue-700';

    // Green: Confirmed (ready to proceed)
    case 'confirmed':
      return 'bg-green-50 text-green-700 hover:bg-green-50 hover:text-green-700';

    // Orange: Pickup states (similar colors for pickup phase)
    case 'in_transit_pickup':
    case 'arrived_pickup':
      return 'bg-orange-100 text-orange-700 hover:bg-orange-100 hover:text-orange-700';

    // Purple: Dropoff states (similar colors for dropoff phase)
    case 'in_transit_dropoff':
    case 'arrived_dropoff':
      return 'bg-purple-100 text-purple-700 hover:bg-purple-100 hover:text-purple-700';

    // Green/Success: Delivered (completion - prominent)
    case 'delivered':
      return 'bg-green-600 text-white hover:bg-green-600 hover:text-white';

    // Red/Error: Canceled (failure)
    case 'canceled':
      return 'bg-red-100 text-red-700 hover:bg-red-100 hover:text-red-700';

    // Default fallback
    default:
      return 'bg-gray-100 text-gray-600 hover:bg-gray-100 hover:text-gray-600';
  }
};

// ─── Transaction Category Colors ─────────────────────────────────────────────

const CUSTOMER_CATEGORY_STYLES: Record<
  TransactionCustomerCategory,
  { badge: string; icon: string }
> = {
  payments: { badge: 'bg-green-100 text-green-700 hover:bg-green-100 hover:text-green-700', icon: 'text-green-600' },
  orders: { badge: 'bg-blue-100 text-blue-700 hover:bg-blue-100 hover:text-blue-700', icon: 'text-blue-600' },
  cashbacks: { badge: 'bg-amber-100 text-amber-700 hover:bg-amber-100 hover:text-amber-700', icon: 'text-amber-600' },
  refunds: { badge: 'bg-blue-100 text-blue-700 hover:bg-blue-100 hover:text-blue-700', icon: 'text-blue-600' },
};

const CARRIER_CATEGORY_STYLES: Record<
  TransactionCarrierCategory,
  { badge: string; icon: string }
> = {
  earnings: { badge: 'bg-green-100 text-green-700 hover:bg-green-100 hover:text-green-700', icon: 'text-green-600' },
  payouts: { badge: 'bg-blue-100 text-blue-700 hover:bg-blue-100 hover:text-blue-700', icon: 'text-blue-600' },
  cashbacks: { badge: 'bg-amber-100 text-amber-700 hover:bg-amber-100 hover:text-amber-700', icon: 'text-amber-600' },
  refunds: { badge: 'bg-purple-100 text-purple-700 hover:bg-purple-100 hover:text-purple-700', icon: 'text-purple-600' },
};

const DEFAULT_CATEGORY_STYLE = { badge: 'bg-gray-100 text-gray-600 hover:bg-gray-100 hover:text-gray-600', icon: 'text-gray-500' };

export const getTransactionCustomerCategoryClass = (
  category: TransactionCustomerCategory,
): string => (CUSTOMER_CATEGORY_STYLES[category] ?? DEFAULT_CATEGORY_STYLE).badge;

export const getTransactionCustomerCategoryIconClass = (
  category: TransactionCustomerCategory,
): string => (CUSTOMER_CATEGORY_STYLES[category] ?? DEFAULT_CATEGORY_STYLE).icon;

export const getTransactionCarrierCategoryClass = (
  category: TransactionCarrierCategory,
): string => (CARRIER_CATEGORY_STYLES[category] ?? DEFAULT_CATEGORY_STYLE).badge;

export const getTransactionCarrierCategoryIconClass = (
  category: TransactionCarrierCategory,
): string => (CARRIER_CATEGORY_STYLES[category] ?? DEFAULT_CATEGORY_STYLE).icon;

// ─── Transaction Status Colors ───────────────────────────────────────────────

export const getTransactionStatusBadgeClass = (status: TransactionStatus): string => {
  switch (status) {
    case 'success':
      return 'bg-green-100 text-green-700 hover:bg-green-100 hover:text-green-700';
    case 'pending':
      return 'bg-orange-100 text-orange-600 hover:bg-orange-100 hover:text-orange-600';
    case 'waiting_for_approval':
      return 'bg-amber-100 text-amber-700 hover:bg-amber-100 hover:text-amber-700';
    case 'failed':
      return 'bg-red-100 text-red-600 hover:bg-red-100 hover:text-red-600';
    default:
      return 'bg-gray-100 text-gray-600 hover:bg-gray-100 hover:text-gray-600';
  }
};
